import { Language, LastPlayedSong } from '@yleisradio/areena-types';
import { DialogTitle } from 'components/Dialog/Dialog';
import { DSNotification } from 'components/DSNotification';
import { Time } from 'components/Time';
import { usePlayerState } from 'contexts/PlayerStateContext';
import { useTranslation } from 'hooks/useTranslation';
import { useUILanguage } from 'hooks/useUILanguage';
import { fetchLastPlayedSongs } from 'services/areena-api/fetchers';
import useSWR from 'swr';
import { groupBy } from 'utils/group-by';
import { isRadioChannel } from 'utils/item';
import { formatDateInHelsinkiTimeZone as formatDate } from 'utils/time';
import styles from './LastPlayedSongsList.module.scss';

function formatSongDate(song: LastPlayedSong, language: Language): string {
  const rawTime = song.time?.raw;

  if (!rawTime) throw new Error('Song time is missing');

  return formatDate(rawTime, language);
}

const DateSeparator = ({ date }: { date: string }) => (
  <tr>
    <th colSpan={2} scope="rowgroup" className={styles.date}>
      {date}
    </th>
  </tr>
);

const SongDetails = ({ song }: { song: LastPlayedSong }) => (
  <tr>
    <td className={styles.time}>
      <Time date={song.time?.raw} />
    </td>
    <td className={styles.rightSide}>
      {song.description ? (
        <div className={styles.description}>{song.description}</div>
      ) : (
        <>
          <div className={styles.title}>{song.title}</div>
          <div className={styles.performer}>{song.performer}</div>
        </>
      )}
    </td>
  </tr>
);

type RequestKey = ['last-played-songs', serviceId: string];

const fetcher: (
  args: RequestKey
) => Promise<LastPlayedSong[] | undefined> = async ([, serviceId]) => {
  return fetchLastPlayedSongs(serviceId);
};

export const LastPlayedSongsList: React.FC = () => {
  const { activePlayer } = usePlayerState();
  const t = useTranslation();
  const language = useUILanguage();

  const serviceId =
    activePlayer && isRadioChannel(activePlayer.item)
      ? activePlayer.item.id
      : undefined;

  const { data: songsList, error } = useSWR(
    serviceId ? (['last-played-songs', serviceId] satisfies RequestKey) : null,
    fetcher,
    {
      refreshInterval: 60_000,
      revalidateOnFocus: true,
    }
  );

  const songsByDate =
    songsList && groupBy(songsList, (song) => formatSongDate(song, language));

  const today = formatDate(new Date(), language);

  return (
    <div className={styles.root}>
      <DialogTitle asChild>
        <h2 className={styles.heading}>{t('songs')}</h2>
      </DialogTitle>

      {error && (
        <DSNotification
          state="primary"
          title={t('genericError')}
          type="error"
        />
      )}

      {songsByDate && (
        <table className={styles.songs}>
          {Object.entries(songsByDate).map(([date, songs]) => (
            <tbody key={date}>
              {date !== today && <DateSeparator date={date} />}
              {songs.map((song) => (
                <SongDetails key={song.time?.raw} song={song} />
              ))}
            </tbody>
          ))}
        </table>
      )}
    </div>
  );
};
